import { useState, useEffect, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import Sidebar from '../components/Shared/Sidebar'
import Topbar from '../components/Shared/Topbar'
import HelpPanel from '../components/Shared/HelpPanel'
import usePushNotifications from '../hooks/usePushNotifications'
import useSlaPolicyLoader from '../hooks/useSlaPolicyLoader'

/**
 * Layout principal con el esqueleto del Design System (SiteFlow), el mismo que
 * usan las demás apps internas:
 *
 *   .admin-page-premium → sidebar + .cms-main-content (topbar + .page-content)
 *
 * En escritorio la sidebar se pliega a iconos (se recuerda en localStorage) y en
 * móvil se abre como cajón con overlay. Los colores salen de los tokens: acá no
 * se escribe ningún color a mano.
 *
 * @component
 * @param {Object} [props]
 * @param {React.ReactNode} [props.children] - Página a renderizar. Si se omite,
 *   se usa el `<Outlet />` de las rutas anidadas.
 * @returns {JSX.Element}
 */
const MainLayout = ({ children }) => {
  /** Cajón abierto en móvil. */
  const [sidebarOpen, setSidebarOpen] = useState(false)

  /** Modo icono en escritorio; se recuerda entre sesiones. */
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  )


  // Deja vigente la política de SLA configurada (Configuración → Tiempos de SLA).
  useSlaPolicyLoader()

  /** Notificaciones push del navegador. */
  const push = usePushNotifications()
  /** Evita volver a pedir el permiso más de una vez por sesión de página. */
  const pushPrompted = useRef(false)

  // Al entrar a la app (usuario ya autenticado), se pide permiso y se suscribe
  // automáticamente si el navegador lo soporta y aún no se ha decidido.
  useEffect(() => {
    if (pushPrompted.current) return
    if (
      push.supported &&
      push.permission === 'default' &&
      !push.isSubscribed &&
      !push.loading
    ) {
      pushPrompted.current = true
      push.subscribe()
    }
  }, [push.supported, push.permission, push.isSubscribed, push.loading, push])

  /** En móvil el botón abre el cajón; en escritorio pliega la sidebar a iconos. */
  const toggleSidebar = () => {
    if (window.matchMedia('(max-width: 991.98px)').matches) {
      setSidebarOpen((v) => !v)
      return
    }
    setCollapsed((prev) => {
      const next = !prev
      localStorage.setItem('sidebarCollapsed', String(next))
      return next
    })
  }

  const closeSidebar = () => setSidebarOpen(false)

  // Con el cajón abierto no se desplaza el fondo (lo pide el shell del DS).
  useEffect(() => {
    document.body.classList.toggle('sidebar-open', sidebarOpen)
    return () => document.body.classList.remove('sidebar-open')
  }, [sidebarOpen])

  return (
    <div className="admin-page-premium">
      <Sidebar
        isOpen={sidebarOpen}
        collapsed={collapsed}
        onClose={closeSidebar}
      />

      <div className="cms-main-content">
        <Topbar toggleSidebar={toggleSidebar} />

        <main className="page-content main-page">
          {children ?? <Outlet />}
        </main>
      </div>

      {/* Ayuda de la pantalla actual: se incluye una sola vez acá, así el botón
          «?» del topbar funciona en todas las pantallas. */}
      <HelpPanel />

      <style>
        {`
          /* Páginas de alto completo (p. ej. el tablero Kanban): la raíz de la
             página lleva .page-fill, ocupa exactamente el área de trabajo y su
             contenido scrollea por dentro, sin scroll de página. */
          .main-page:has(> .page-fill) {
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          .page-fill {
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }
          /* En pantallas muy bajas se vuelve al scroll normal de página. */
          @media (max-height: 560px) {
            .main-page:has(> .page-fill) { overflow: auto; }
          }
        `}
      </style>
    </div>
  )
}

export default MainLayout
